// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the starting points (suggested requests shown where people work) as pure
// functions: the wording for a starter and its context, the chips in the request,
// the connections each one reads, the tooltip, who gets which starters in Mail, and
// the context line the request carries into the Assistant DM. No stores: callers pass
// what they know, including the Lingui instance for the words people see (the request,
// chips and tooltips). The context line stays plain English: only the assistant reads it.
// Unit tested in starters.test.ts.

import type {Starter} from '@fluxer/schema/src/domains/konvo/AssistantEngagement';
import {isAutomatedMail, isNewsletterMail} from '@fluxer/schema/src/domains/konvo/HomeFeedModel';
import type {I18n, MessageDescriptor} from '@lingui/core';
import {msg} from '@lingui/core/macro';

const REQ_CLIENT_NEEDS_BEFORE = msg({
	message: 'Find everything ',
	comment:
		'Konvo panel request, first part (keep the trailing space). Followed by a client name chip, then "still needs from us, oldest first."',
});
const REQ_CLIENT_NEEDS_AFTER = msg({
	message: ' still needs from us, oldest first.',
	comment: 'Konvo panel request, last part (keep the leading space). Comes after the client name chip.',
});
const REQ_TASK_SUBJECT = msg({
	message: 'Create a task from this email: {subject}.',
	comment: 'Konvo panel request from the mail reader. {subject} is the email subject.',
});
const REQ_TASK = msg({message: 'Create a task from this email.', comment: 'Konvo panel request from the mail reader.'});
const REQ_DRAFT_TO = msg({
	message: 'Draft a reply to {name} about this email.',
	comment: 'Konvo panel request from the mail reader. {name} is the sender first name.',
});
const REQ_DRAFT = msg({message: 'Draft a reply to this email.', comment: 'Konvo panel request from the mail reader.'});
const REQ_OUTSTANDING = msg({
	message: 'Find everything I still owe people: tasks, promises in mail and chats, and things I’m waiting on.',
	comment: 'Konvo panel request from the Tasks header.',
});
const REQ_REVIEW_TIME = msg({
	message: 'Review my tracked time this week and find anything I forgot to log.',
	comment: 'Konvo panel request from the Tasks header.',
});
const REQ_MEETING_BEFORE = msg({
	message: 'Prepare me for ',
	comment: 'Konvo panel meeting request, first part (keep the trailing space). Followed by the meeting name chip.',
});
const REQ_MEETING_AFTER_AT = msg({
	message: ' at {time}. What’s open, what happened last time, and what I should raise.',
	comment:
		'Konvo panel meeting request, last part after the meeting name chip (keep the leading space). {time} is the start time, e.g. 14:30.',
});
const REQ_MEETING_AFTER = msg({
	message: '. What’s open, what happened last time, and what I should raise.',
	comment: 'Konvo panel meeting request, last part after the meeting name chip, when the start time is unknown.',
});
const THIS_MEETING = msg({
	message: 'this meeting',
	comment: 'Meeting name chip in the Konvo panel request when the meeting has no subject.',
});
const THIS_SENDER = msg({
	message: 'this sender',
	comment: 'Client chip in the Konvo panel request when neither a company nor a sender name is known.',
});
const RANGE_30 = msg({message: 'Last 30 days', comment: 'Time range chip in the Konvo panel request.'});
const RANGE_90 = msg({message: 'Last 90 days', comment: 'Time range chip in the Konvo panel request.'});
const RANGE_UP_TO = msg({
	message: 'Up to {day}',
	comment: 'Time range chip in the Konvo panel request. {day} is a short date, e.g. "Fri 25 Sept".',
});
const RANGE_SPAN = msg({
	message: '{from} – {to}',
	comment: 'Time range chip in the Konvo panel request. {from} and {to} are short dates, e.g. "Mon 21 Sept".',
});
const PEOPLE_MORE = msg({
	message: '{names} +{count}',
	comment:
		'People chip in the Konvo panel request when there are more than three: two first names, then how many more.',
});
const ME = msg({message: 'Me', comment: 'People chip in the Konvo panel request for "Find my outstanding work".'});
const CONNECTION_DESCRIPTORS: Record<StarterConnection, MessageDescriptor> = {
	mail: msg({message: 'Mail', comment: 'Connection name in the Konvo panel "Uses" row and starter tooltips.'}),
	clickup: msg({message: 'ClickUp', comment: 'Connection name (brand) in the Konvo panel "Uses" row.'}),
	clickup_time: msg({
		message: 'ClickUp time',
		comment: 'Connection name in the Konvo panel "Uses" row: time tracked in ClickUp.',
	}),
	whatsapp: msg({message: 'WhatsApp', comment: 'Connection name (brand) in the Konvo panel "Uses" row.'}),
	calendar: msg({message: 'Calendar', comment: 'Connection name in the Konvo panel "Uses" row.'}),
	notes: msg({message: 'Notes', comment: 'Connection name in the Konvo panel "Uses" row.'}),
	konvo: msg({message: 'Konvo', comment: 'Connection name (brand) in the Konvo panel "Uses" row.'}),
};
const TIP_CLIENT = msg({
	message: 'Looks for open requests from {who}',
	comment: 'Tooltip on the "Find what <client> still needs" starting point. {who} is a mail domain or first name.',
});
const TIP_SOURCES_90 = msg({
	message: '{sources} · last 90 days',
	comment: 'Tooltip line on a starting point: the sources it searches, then the time range.',
});
const TIP_SOURCES_WEEK = msg({
	message: '{sources} · this week',
	comment: 'Tooltip line on a starting point: the sources it searches, then the time range.',
});
const TIP_TASK = msg({
	message: 'Drafts a ClickUp task from this email. Nothing is created until you confirm.',
	comment: 'Tooltip on the "Create a task from this email" starting point.',
});
const TIP_DRAFT = msg({
	message: 'Writes a reply in your voice from this thread',
	comment: 'Tooltip on the "Draft a reply" starting point.',
});
const TIP_OUTSTANDING = msg({
	message: 'Tasks, promises you made in mail and chats, and things you are waiting on',
	comment: 'Tooltip on the "Find my outstanding work" starting point.',
});
const TIP_REVIEW_TIME = msg({
	message: 'Compares your ClickUp time with your calendar and task activity',
	comment: 'Tooltip on the "Review my time" starting point.',
});
const TIP_MEETING = msg({
	message: 'What is open with these people, what happened last time, and what to raise',
	comment: 'Tooltip on the "Prepare me for this meeting" button.',
});

/** Context keys a starter understands (all optional strings, as in `PanelOpenOptions.context`). */
export interface StarterContext {
	/** Mail: the message, its conversation and sender. */
	mail_id?: string;
	conversation_id?: string;
	subject?: string;
	from_name?: string;
	from_address?: string;
	/** Mail: the client (company name) and its domain; empty for personal senders. */
	client?: string;
	client_domain?: string;
	/** Calendar: the meeting. */
	meeting_id?: string;
	meeting_subject?: string;
	/** ISO start time of the meeting. */
	meeting_start?: string;
	/** Comma-separated attendee names (without the user). */
	people?: string;
	/** Time range the request covers, a RangeKey. */
	range?: string;
	/** Quick tasks: a chat by the assistant's conversation id (konvo:…, teams:…, whatsapp:…) and its name. */
	conversation?: string;
	chat_name?: string;
	/** Ask about this chat (phones): one message of that chat, by id, with its author and text. */
	message_id?: string;
	message_author?: string;
	message_text?: string;
	/** Ask about this chat: a drafted reply comes alone in one quote block, so the sheet can put it in the composer. */
	reply_draft?: string;
	/** Quick tasks: a ClickUp task. */
	task_id?: string;
	task_name?: string;
	/** Agents: the coding-agent thread open on the page, by machine and thread id. */
	agent_machine?: string;
	agent_thread?: string;
	/** How a machine is doing, as its page on Agents shows it (one line of numbers). */
	agent_stats?: string;
	agent_title?: string;
	/** Quick tasks: what came in during the last hour, as "id (name)" pairs. */
	recent?: string;
	/** A question typed in the panel: the page it was asked from carried this context. */
	on_screen?: string;
}

export type StarterConnection = 'mail' | 'clickup' | 'clickup_time' | 'whatsapp' | 'calendar' | 'notes' | 'konvo';

export type RequestPart = {kind: 'text'; text: string} | {kind: 'token'; text: string; icon: 'client' | 'meeting'};

export type RangeKey = 'last_30_days' | 'last_90_days' | 'this_week' | 'to_end_of_week';

export interface ScopeChip {
	key: 'range' | 'people';
	icon: 'calendar' | 'people';
	label: string;
}

/** Big webmail providers: a sender there is a person, not a company. */
const PUBLIC_MAIL_DOMAINS = new Set([
	'gmail.com',
	'googlemail.com',
	'outlook.com',
	'hotmail.com',
	'live.com',
	'msn.com',
	'yahoo.com',
	'yahoo.co.uk',
	'icloud.com',
	'me.com',
	'mac.com',
	'aol.com',
	'gmx.com',
	'gmx.net',
	'proton.me',
	'protonmail.com',
	'mweb.co.za',
	'telkomsa.net',
	'vodamail.co.za',
	'webmail.co.za',
	'iafrica.com',
	'absamail.co.za',
	'lantic.net',
]);

/** Second-level labels that sit under a country code (co.za, com.au, org.uk). */
const SECOND_LEVEL = new Set(['co', 'com', 'org', 'net', 'gov', 'ac', 'edu', 'ltd', 'plc']);

export function mailDomain(address: string | null | undefined): string | null {
	const at = address?.lastIndexOf('@') ?? -1;
	if (!address || at < 0) return null;
	const domain = address
		.slice(at + 1)
		.trim()
		.toLowerCase();
	return domain || null;
}

export function isPublicMailDomain(domain: string | null | undefined): boolean {
	return !!domain && PUBLIC_MAIL_DOMAINS.has(domain.toLowerCase());
}

/** "mail.colourtech.co.za" → "colourtech": the label that names the organisation. */
export function domainLabel(domain: string): string {
	const labels = domain.toLowerCase().split('.').filter(Boolean);
	if (labels.length <= 1) return labels[0] ?? domain;
	let end = labels.length - 1;
	if (end >= 2 && labels[end].length === 2 && SECOND_LEVEL.has(labels[end - 1])) end -= 1;
	return labels[end - 1] ?? labels[0];
}

const compact = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * The client's name for a sender domain. A known name (ClickUp spaces and lists) whose
 * letters match the domain wins ("mountainhops.co.za" → "Mountain Hops"); otherwise
 * the domain label with a capital ("colourtech.co.za" → "Colourtech").
 */
export function clientNameForDomain(domain: string, knownNames: ReadonlyArray<string> = []): string {
	const label = domainLabel(domain);
	const key = compact(label);
	const exact = knownNames.find((name) => compact(name) === key);
	if (exact) return exact.trim();
	const prefix = knownNames.find((name) => {
		const known = compact(name);
		return known.length >= 4 && key.length >= 4 && (known.startsWith(key) || key.startsWith(known));
	});
	if (prefix) return prefix.trim();
	return label.charAt(0).toUpperCase() + label.slice(1);
}

export function firstName(name: string | null | undefined, address?: string | null): string {
	const trimmed = name?.trim();
	if (trimmed) {
		// "Naidoo, Sarah" is common in Outlook directories.
		if (trimmed.includes(',')) return trimmed.split(',')[1]?.trim().split(/\s+/)[0] || trimmed;
		return trimmed.split(/\s+/)[0];
	}
	const local = address?.split('@')[0] ?? '';
	const part = local.split(/[._-]/)[0] ?? '';
	return part ? part.charAt(0).toUpperCase() + part.slice(1) : '';
}

export interface MailStarterInput {
	subject: string;
	preview?: string;
	from_name: string | null;
	from_address: string | null;
	/** The user's own address, to tell internal mail apart. */
	self_address: string | null;
	/** ClickUp space and list names, for the client name. */
	known_names?: ReadonlyArray<string>;
}

export interface MailStarterPlan {
	starters: Array<Starter>;
	/** Company name for outside mail from a company domain; null for personal and internal senders. */
	client: string | null;
	client_domain: string | null;
	first_name: string;
	internal: boolean;
}

/**
 * Which starters the mail reading pane offers: none for automated senders and newsletters
 * (and the user's own mail), "Create a task" and "Draft a reply" for internal mail, and all
 * three for outside mail ("Find what <client> still needs", or the sender's first name when
 * they write from a personal address).
 */
export function mailStarterPlan(input: MailStarterInput): MailStarterPlan {
	const domain = mailDomain(input.from_address);
	const selfDomain = mailDomain(input.self_address);
	const first = firstName(input.from_name, input.from_address);
	const none: MailStarterPlan = {starters: [], client: null, client_domain: null, first_name: first, internal: false};
	if (!domain) return none;
	if (input.self_address && input.from_address?.trim().toLowerCase() === input.self_address.trim().toLowerCase())
		return none;
	if (
		isAutomatedMail({from_address: input.from_address, subject: input.subject}) ||
		isNewsletterMail({from_address: input.from_address, subject: input.subject, preview: input.preview ?? ''})
	)
		return none;
	const internal = selfDomain != null && domain === selfDomain;
	if (internal) return {...none, starters: ['task_from_email', 'draft_reply'], internal: true};
	if (isPublicMailDomain(domain)) {
		return {...none, starters: ['client_needs', 'task_from_email', 'draft_reply']};
	}
	return {
		starters: ['client_needs', 'task_from_email', 'draft_reply'],
		client: clientNameForDomain(domain, input.known_names ?? []),
		client_domain: domain,
		first_name: first,
		internal: false,
	};
}

/** Who a client_needs request is about: the client, or the person for personal mail. */
export function clientSubject(i18n: I18n, context: StarterContext): string {
	return context.client?.trim() || firstName(context.from_name, context.from_address) || i18n._(THIS_SENDER);
}

function clockTime(iso: string | undefined, locale: string): string {
	if (!iso) return '';
	const date = new Date(iso);
	if (Number.isNaN(date.getTime())) return '';
	return date.toLocaleTimeString(locale, {hour: '2-digit', minute: '2-digit'});
}

/** The request as text with chips, as the panel shows it before it runs (in the active locale). */
export function requestParts(i18n: I18n, starter: Starter, context: StarterContext): Array<RequestPart> {
	const text = (descriptor: MessageDescriptor, values?: Record<string, string>): RequestPart => ({
		kind: 'text',
		text: i18n._(descriptor, values),
	});
	switch (starter) {
		case 'client_needs':
			return [
				text(REQ_CLIENT_NEEDS_BEFORE),
				{kind: 'token', text: clientSubject(i18n, context), icon: 'client'},
				text(REQ_CLIENT_NEEDS_AFTER),
			];
		case 'task_from_email': {
			const subject = context.subject?.trim();
			return [subject ? text(REQ_TASK_SUBJECT, {subject}) : text(REQ_TASK)];
		}
		case 'draft_reply': {
			const name = firstName(context.from_name, context.from_address);
			return [name ? text(REQ_DRAFT_TO, {name}) : text(REQ_DRAFT)];
		}
		case 'outstanding_work':
			return [text(REQ_OUTSTANDING)];
		case 'review_time':
			return [text(REQ_REVIEW_TIME)];
		case 'meeting_prep': {
			const time = clockTime(context.meeting_start, i18n.locale);
			return [
				text(REQ_MEETING_BEFORE),
				{kind: 'token', text: context.meeting_subject?.trim() || i18n._(THIS_MEETING), icon: 'meeting'},
				time ? text(REQ_MEETING_AFTER_AT, {time}) : text(REQ_MEETING_AFTER),
			];
		}
		case 'free':
			return [];
	}
}

export function partsText(parts: ReadonlyArray<RequestPart>): string {
	return parts.map((part) => part.text).join('');
}

/** The default request text for a starter. */
export function starterRequest(i18n: I18n, starter: Starter, context: StarterContext): string {
	return partsText(requestParts(i18n, starter, context));
}

/** The time range a starter covers when the context does not say. */
export function defaultRange(starter: Starter): RangeKey | null {
	switch (starter) {
		case 'client_needs':
			return 'last_90_days';
		case 'meeting_prep':
			return 'last_30_days';
		case 'outstanding_work':
			return 'to_end_of_week';
		case 'review_time':
			return 'this_week';
		default:
			return null;
	}
}

const DAY_MS = 86_400_000;

function startOfWeek(now: Date): Date {
	const day = new Date(now.getFullYear(), now.getMonth(), now.getDate());
	const offset = (day.getDay() + 6) % 7;
	return new Date(day.getTime() - offset * DAY_MS);
}

function shortDay(date: Date, locale: string): string {
	return date.toLocaleDateString(locale, {weekday: 'short', day: 'numeric', month: 'short'});
}

function isoDay(date: Date): string {
	const pad = (value: number) => String(value).padStart(2, '0');
	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function weekBounds(now: Date): {monday: Date; friday: Date} {
	const monday = startOfWeek(now);
	return {monday, friday: new Date(monday.getTime() + 4 * DAY_MS)};
}

/** "Last 90 days", "Up to Fri 25 Sept", "Mon 21 Sept – Fri 25 Sept", in the active locale. */
export function rangeLabel(i18n: I18n, range: RangeKey, now: Date): string {
	const {monday, friday} = weekBounds(now);
	switch (range) {
		case 'last_30_days':
			return i18n._(RANGE_30);
		case 'last_90_days':
			return i18n._(RANGE_90);
		case 'to_end_of_week':
			return i18n._(RANGE_UP_TO, {day: shortDay(friday, i18n.locale)});
		case 'this_week':
			return i18n._(RANGE_SPAN, {from: shortDay(monday, i18n.locale), to: shortDay(friday, i18n.locale)});
	}
}

/** The range for the context line, in plain English with ISO dates (only the assistant reads it). */
export function rangeContext(range: RangeKey, now: Date): string {
	const {monday, friday} = weekBounds(now);
	switch (range) {
		case 'last_30_days':
			return 'last 30 days';
		case 'last_90_days':
			return 'last 90 days';
		case 'to_end_of_week':
			return `up to ${isoDay(friday)}`;
		case 'this_week':
			return `${isoDay(monday)} to ${isoDay(friday)}`;
	}
}

export const RANGE_CHOICES: Record<Starter, ReadonlyArray<RangeKey>> = {
	client_needs: ['last_30_days', 'last_90_days'],
	meeting_prep: ['last_30_days', 'last_90_days'],
	outstanding_work: ['to_end_of_week', 'last_30_days'],
	review_time: ['this_week', 'last_30_days'],
	task_from_email: [],
	draft_reply: [],
	free: [],
};

export function isRangeKey(value: string | undefined): value is RangeKey {
	return value === 'last_30_days' || value === 'last_90_days' || value === 'this_week' || value === 'to_end_of_week';
}

/** Names as a short chip: "Sarah, Thandi, Felix" or "Sarah, Thandi +2". */
export function peopleLabel(i18n: I18n, names: ReadonlyArray<string>): string {
	const firsts = names.map((name) => firstName(name)).filter(Boolean);
	if (firsts.length <= 3) return firsts.join(', ');
	return i18n._(PEOPLE_MORE, {names: firsts.slice(0, 2).join(', '), count: String(firsts.length - 2)});
}

/**
 * People travel in a context value as a JSON array, because display names can contain
 * commas ("Naidoo, Sarah"). A plain string (older server contexts) is split on commas.
 */
export function joinPeople(names: ReadonlyArray<string>): string {
	return JSON.stringify(names.map((name) => name.trim()).filter(Boolean));
}

export function splitPeople(value: string | undefined): Array<string> {
	const text = value?.trim() ?? '';
	if (text.startsWith('[')) {
		try {
			const parsed: unknown = JSON.parse(text);
			if (Array.isArray(parsed))
				return parsed
					.filter((name): name is string => typeof name === 'string' && name.trim() !== '')
					.map((name) => name.trim());
		} catch {
			// Not JSON after all: fall through to the comma list.
		}
	}
	return text
		.split(',')
		.map((name) => name.trim())
		.filter(Boolean);
}

/** The scope chips under the request: time range and people. */
export function scopeChips(i18n: I18n, starter: Starter, context: StarterContext, now: Date): Array<ScopeChip> {
	const chips: Array<ScopeChip> = [];
	const range = isRangeKey(context.range) ? context.range : defaultRange(starter);
	const people = splitPeople(context.people);
	if (starter === 'meeting_prep' && people.length > 0) {
		chips.push({key: 'people', icon: 'people', label: peopleLabel(i18n, people)});
	}
	if (range) chips.push({key: 'range', icon: 'calendar', label: rangeLabel(i18n, range, now)});
	if (starter === 'client_needs' && people.length > 0) {
		chips.push({key: 'people', icon: 'people', label: peopleLabel(i18n, people)});
	}
	if (starter === 'outstanding_work') chips.push({key: 'people', icon: 'people', label: i18n._(ME)});
	return chips;
}

/** The connections a starter reads, in the order the "Uses" row shows them. */
export function starterUses(starter: Starter): Array<StarterConnection> {
	switch (starter) {
		case 'client_needs':
			return ['mail', 'clickup', 'whatsapp'];
		case 'task_from_email':
			return ['mail', 'clickup'];
		case 'draft_reply':
			return ['mail'];
		case 'outstanding_work':
			return ['clickup', 'mail', 'whatsapp', 'konvo'];
		case 'review_time':
			return ['clickup_time', 'calendar', 'mail'];
		case 'meeting_prep':
			return ['calendar', 'mail', 'clickup', 'notes'];
		case 'free':
			return [];
	}
}

/** A connection's name as the "Uses" row and the tooltips show it. */
export function connectionName(i18n: I18n, connection: StarterConnection): string {
	return i18n._(CONNECTION_DESCRIPTORS[connection]);
}

/** What the tooltip on a starter says it searches: a line and the sources under it. */
export function starterTooltip(i18n: I18n, starter: Starter, context: StarterContext): {line: string; sources: string} {
	const sources = starterUses(starter)
		.map((connection) => connectionName(i18n, connection))
		.join(' · ');
	switch (starter) {
		case 'client_needs': {
			const domain = context.client_domain || mailDomain(context.from_address) || '';
			const who = context.client_domain ? domain : firstName(context.from_name, context.from_address) || domain;
			return {line: i18n._(TIP_CLIENT, {who}), sources: i18n._(TIP_SOURCES_90, {sources})};
		}
		case 'task_from_email':
			return {line: i18n._(TIP_TASK), sources};
		case 'draft_reply':
			return {line: i18n._(TIP_DRAFT), sources};
		case 'outstanding_work':
			return {line: i18n._(TIP_OUTSTANDING), sources};
		case 'review_time':
			return {line: i18n._(TIP_REVIEW_TIME), sources: i18n._(TIP_SOURCES_WEEK, {sources})};
		case 'meeting_prep':
			return {line: i18n._(TIP_MEETING), sources};
		case 'free':
			return {line: '', sources: ''};
	}
}

/** Meetings worth the filled "Prepare me" button: outside guests, or three or more people. */
export function meetingWorthPrep(
	attendees: ReadonlyArray<{address: string | null}>,
	selfAddress: string | null,
): {worth: boolean; outside: boolean} {
	const selfDomain = mailDomain(selfAddress);
	const self = selfAddress?.trim().toLowerCase() ?? null;
	const others = attendees.filter((attendee) => attendee.address && attendee.address.toLowerCase() !== self);
	const outside =
		selfDomain != null &&
		others.some((attendee) => {
			const domain = mailDomain(attendee.address);
			return domain != null && domain !== selfDomain;
		});
	return {worth: outside || others.length + 1 >= 3, outside};
}

/** Minutes until a start time, or null when it has started or is more than a day away. */
export function minutesUntil(startIso: string | null | undefined, now: number): number | null {
	if (!startIso) return null;
	const start = Date.parse(startIso);
	if (!Number.isFinite(start)) return null;
	const minutes = Math.round((start - now) / 60_000);
	return minutes > 0 && minutes <= 24 * 60 ? minutes : null;
}

const clean = (value: string, max = 160) => value.replace(/\s+/g, ' ').replace(/·/g, '-').trim().slice(0, max);

/**
 * The line a panel request carries into the Assistant DM (rendered small, as Markdown
 * subtext): which starting point it is and what it is about, with the ids the assistant's
 * tools need. The server prompt knows the "Konvo panel ·" prefix.
 */
export function panelContextLine(starter: Starter, context: StarterContext, extra: {range?: string} = {}): string {
	const parts: Array<string> = [`Konvo panel · ${starter}`];
	if (context.subject || context.mail_id) {
		const sender = [context.from_name, context.from_address ? `<${context.from_address}>` : null]
			.filter(Boolean)
			.join(' ');
		parts.push(`email "${clean(context.subject ?? '', 120)}"${sender ? ` from ${clean(sender, 120)}` : ''}`);
		if (context.mail_id) parts.push(`mail id ${clean(context.mail_id, 200)}`);
		if (context.conversation_id) parts.push(`conversation ${clean(context.conversation_id, 200)}`);
	}
	if (context.client) {
		parts.push(`client ${clean(context.client, 80)}${context.client_domain ? ` (${context.client_domain})` : ''}`);
	}
	if (context.meeting_subject || context.meeting_id) {
		parts.push(
			`meeting "${clean(context.meeting_subject ?? '', 120)}"${context.meeting_start ? ` at ${context.meeting_start}` : ''}`,
		);
		if (context.meeting_id) parts.push(`event id ${clean(context.meeting_id, 200)}`);
	}
	if (context.conversation) {
		parts.push(`chat "${clean(context.chat_name ?? '', 80)}" conversation ${clean(context.conversation, 200)}`);
	}
	if (context.message_id) {
		const author = context.message_author ? ` from ${clean(context.message_author, 80)}` : '';
		const text = context.message_text ? `: "${clean(context.message_text, 300)}"` : '';
		parts.push(`about message ${clean(context.message_id, 80)}${author}${text}`);
	}
	if (context.reply_draft) parts.push('a drafted reply goes alone in one > quote block, in the language of the chat');
	if (context.task_id)
		parts.push(`clickup task "${clean(context.task_name ?? '', 120)}" id ${clean(context.task_id, 80)}`);
	if (context.agent_thread && context.agent_machine) {
		parts.push(
			`agent thread "${clean(context.agent_title ?? '', 120)}" machine ${clean(context.agent_machine, 80)} thread ${clean(context.agent_thread, 120)}`,
		);
	}
	if (context.agent_stats) parts.push(`machine stats: ${clean(context.agent_stats, 700)}`);
	if (context.recent) parts.push(`last hour: ${clean(context.recent, 900)}`);
	if (context.on_screen) parts.push('on screen when asked');
	if (extra.range) parts.push(clean(extra.range, 60).toLowerCase());
	const people = splitPeople(context.people);
	if (people.length > 0) parts.push(`people ${clean(people.join(', '), 200)}`);
	return `-# ${parts.join(' · ')}`;
}

/** The whole message a panel run sends: the request, a blank line, the context line. */
export function panelMessage(request: string, contextLine: string | null): string {
	const text = request.trim();
	return contextLine ? `${text}\n\n${contextLine}` : text;
}

export {stripPanelContext} from '@app/features/konvo_panel/state/PanelContextLine';

/**
 * Context from the server (corner pop-ups, teaser "tries", nudge cards) uses short keys
 * (`sender`, `name`, `subject`, `start`); the panel reads the mail and meeting ones.
 * Fills the panel keys without overwriting any that are already set.
 */
export function normalizeStarterContext(
	starter: Starter,
	raw: Record<string, string> | undefined,
): Record<string, string> {
	const context: Record<string, string> = {...(raw ?? {})};
	const fill = (key: string, value: string | undefined) => {
		if (value && !context[key]) context[key] = value;
	};
	if (starter === 'meeting_prep') {
		fill('meeting_subject', raw?.subject);
		fill('meeting_start', raw?.start);
	} else {
		fill('from_address', raw?.sender);
		fill('from_name', raw?.name);
	}
	return context;
}
